import { useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { apiUsersQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  useExtendDelete,
  useExtendMutation,
  useExtendQuery,
} from 'src/queries/queries.utils'
import {
  createSetIsAPIKeyCreatedPopupOpen,
  createSetIsDeleteAPIKeyPopupOpen,
} from 'src/redux/actions/app.actions'

export const formatDateString = (dateString: string): string => {
  const date = new Date(dateString)

  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const year = date.getFullYear().toString().slice(-2)

  return `${month}/${day}/${year}`
}

export enum APIUserStatus {
  ACTIVE = 'ACTIVE',
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  DENIED = 'DENIED',
}

export type APIKeyResponse = {
  id: string
  secretId: string
  expires: string
  createdAt: string
}

export type APIKeyCreatedResponse = {
  apiKeyId: string
  apiSecret: string
  expiresAt: string
}

export type GetAPIKeysSuccessPayload = {
  apiKeys: APIKeyResponse[]
  apiKeyCount: number
  remainingApiKeyQuota: number
}

export type APIUserResponse = {
  apiUserId: string
  isActive: boolean
  status: APIUserStatus
}

export type DeleteAPIKeyResponse = {
  apiKeyId: string
}

interface QueryAPIUserOptions
  extends ExtendBaseQueryOptions<APIUserResponse, APIUserResponse> {}

export const useQueryAPIUser = ({
  enabled = true,
  ...rest
}: QueryAPIUserOptions) => {
  return useExtendQuery<APIUserResponse, APIUserResponse>({
    queryKey: apiUsersQueryKeys.me(),
    queryUrl: () => `/apikeys/users/`,
    enabled: enabled,
    select: (data: APIUserResponse) => data,
    retry: false,
    ...rest,
  })
}

interface QueryAPIKeysOptions
  extends ExtendBaseQueryOptions<
    GetAPIKeysSuccessPayload,
    GetAPIKeysSuccessPayload
  > {}

export const useQueryAPIKeys = ({
  enabled = true,
  ...rest
}: QueryAPIKeysOptions) => {
  return useExtendQuery<GetAPIKeysSuccessPayload, GetAPIKeysSuccessPayload>({
    queryKey: apiUsersQueryKeys.apiKeys(),
    queryUrl: () => `/apikeys/`,
    enabled: enabled,
    retry: false,
    select: (data: GetAPIKeysSuccessPayload) => data,
    ...rest,
  })
}

export const usePostAPIUser = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<APIUserResponse, { body: object }>(
    MutationMethod.POST,
    {
      mutationUrl: () => `/apikeys/users/`,
      onSuccess: (res) => {
        queryClient.setQueryData(apiUsersQueryKeys.me(), {
          apiUserId: res.apiUserId,
          isActive: false,
        })
      },
    },
  )
}

export const usePostAPIKey = () => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  return useExtendMutation<APIKeyCreatedResponse, { body: object }>(
    MutationMethod.POST,
    {
      mutationUrl: () => `/apikeys/`,
      onSuccess: (res) => {
        queryClient.invalidateQueries({ queryKey: apiUsersQueryKeys.apiKeys() })
        dispatch(
          createSetIsAPIKeyCreatedPopupOpen(true, {
            id: res.apiKeyId,
            secret: res.apiSecret,
            expiry: formatDateString(res.expiresAt),
          }),
        )
      },
    },
  )
}

export const useDeleteAPIKey = () => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  return useExtendDelete<DeleteAPIKeyResponse, { body: { apiKeyId: string } }>({
    mutationUrl: (vars) => `/apikeys/${vars.body.apiKeyId}`,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiUsersQueryKeys.apiKeys() })
      dispatch(createSetIsDeleteAPIKeyPopupOpen(false))
    },
  })
}
